import React, { useState } from 'react';
import { forget, whereAmI } from '../lib/whereAmI';
import './LocationRefused.css';

/**
 * What to do when a change was refused for want of a location.
 *
 * Gordon chose to block the change until the person allows it, over my advice
 * that recording "not available" and carrying on was safer. His call, and the
 * reason I argued the other way is the reason this component exists: a browser
 * permission prompt has a Block button, the refusal sticks until somebody digs
 * through site settings, and the person who hits it is usually standing in a
 * corridor holding a phone with a job to do.
 *
 * So a block has to come with a way out, in words. "Request failed with status
 * code 428" is not a way out. Neither is "allow location access", which tells
 * somebody what to do and not where to do it.
 *
 * WHAT THIS IS NOT. It is not a retry loop and it does not re-ask on a timer.
 * Browsers only show the prompt again when the person has actually changed the
 * setting, so an automatic retry just fails faster and more often. The button
 * is there for the moment after they have changed it.
 */

/** Where the setting lives, in the browsers this firm actually uses. */
const STEPS = {
  denied: [
    'Chrome and Edge: click the icon at the left of the address bar, find '
      + 'Location, and set it to Allow.',
    'Safari on a Mac: Safari menu, then Settings for this website, then set '
      + 'Location to Allow.',
    'iPhone or iPad: Settings, Privacy & Security, Location Services, and '
      + 'check both Safari (or Chrome) and that Location Services is on.',
    'Android: the icon at the left of the address bar, then Permissions, '
      + 'then Location.'
  ],
  insecure: [
    'This page is not on https, and browsers will not report a location on an '
      + 'insecure page. Open Jarvis at its usual https address.'
  ],
  unsupported: [
    'This browser cannot report a location at all. A current Chrome, Edge, '
      + 'Firefox or Safari can.'
  ]
};

const LocationRefused = ({ error, onRetry, onDismiss }) => {
  const [trying, setTrying] = useState(false);
  const [result, setResult] = useState(null);

  // Only a 428 from the gate. Anything else is a different problem wearing
  // this component's clothes, and showing location advice for a 500 sends
  // somebody into their browser settings for no reason.
  const body = error?.response?.status === 428 ? error.response.data : null;
  if (!body) return null;

  const steps = STEPS[body.reason] || STEPS.denied;

  const again = async () => {
    setTrying(true);
    setResult(null);
    // forget() first, or a 'denied' cached eight seconds ago comes straight
    // back and the button looks broken to somebody who has just fixed it.
    forget();
    const where = await whereAmI({ force: true });
    setTrying(false);
    if (where.status === 'recorded') {
      setResult(null);
      if (onRetry) onRetry();
    } else {
      setResult(where.status);
    }
  };

  return (
    <section className="lr" role="alert">
      <h4 className="lr-head">{body.error || 'This change needs your location'}</h4>
      <p className="lr-why">{body.detail}</p>

      <ul className="lr-steps">
        {steps.map((s) => <li key={s}>{s}</li>)}
      </ul>

      {result && (
        <p className="lr-still">
          Still nothing — your browser answered &ldquo;{result}&rdquo;. If you have
          just changed the setting, you may need to reload the page.
        </p>
      )}

      <div className="lr-row">
        <button type="button" className="lr-go" onClick={again} disabled={trying}>
          {trying ? 'Looking…' : 'I’ve allowed it — try again'}
        </button>
        {onDismiss && (
          <button type="button" className="lr-flat" onClick={onDismiss}>
            Leave it for now
          </button>
        )}
      </div>

      {/* Said out loud, because somebody who cannot get past this needs to
          know there is a person who can let them, rather than concluding the
          application is broken. */}
      <p className="lr-out">
        If your device will not cooperate, an administrator can switch this
        requirement off in Settings.
      </p>
    </section>
  );
};

export default LocationRefused;
